import { useEffect, useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const LABELS = { "1m": "1 month", "3m": "3 months", "6m": "6 months", "1y": "1 year", lifetime: "Lifetime" };
const STATUS = { applied: "bg-emerald-100 text-emerald-700", waiting: "bg-amber-100 text-amber-800", revoked: "bg-slate-200 text-slate-600" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

export function FreeAccess() {
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [duration, setDuration] = useState("1y");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/access-grants").then(({ data }) => setD(data)).catch(() => setD({ grants: [], durations: Object.keys(LABELS) }));
  useEffect(() => { load(); }, []);
  const grant = async (e) => {
    e.preventDefault();
    setBusy("grant");
    try {
      const { data } = await api.post("/admin/access-grants", { email, duration, note });
      toast.success(data.status === "applied" ? `NxT Plus unlocked for ${data.email} — no payment needed` : `${data.email} will get Plus automatically the moment they sign up`);
      setEmail(""); setNote(""); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const revoke = async (g) => {
    if (!window.confirm(`Remove free Plus access for ${g.email}?`)) return;
    setBusy(g.id);
    try { await api.delete(`/admin/access-grants/${g.id}`); toast.success(`Access removed for ${g.email}`); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const active = (d?.grants || []).filter((g) => g.status !== "revoked");
  const revoked = (d?.grants || []).filter((g) => g.status === "revoked");
  return (
    <div className="rounded-3xl border border-emerald-200 bg-emerald-50/40 p-6" data-testid="free-access">
      <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><Gift className="h-5 w-5 text-emerald-600" /> Give free Plus access</div>
      <p className="mb-4 text-xs text-muted-foreground">Unlock every NxT Plus feature for any person by email — no payment, no Razorpay. If they haven't signed up yet, Plus switches on automatically the moment they create an account (email or Google) with that address. They get a "You've been given NxT Plus" email. Free grants never count in revenue or subscriber numbers.</p>
      <form onSubmit={grant} className="flex flex-wrap gap-2">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@gmail.com" data-testid="free-access-email"
          className="min-w-[220px] flex-1 rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
        <select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="free-access-duration" className="rounded-xl border border-input bg-card px-3 py-2.5 text-sm">
          {Object.entries(LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. friend, campus ambassador" data-testid="free-access-note"
          className="min-w-[200px] flex-1 rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-emerald-500" />
        <button type="submit" disabled={!!busy} data-testid="free-access-grant-btn" className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-60">{busy === "grant" ? "Unlocking…" : "Unlock Plus for free"}</button>
      </form>
      {!d ? <Loader2 className="mx-auto my-6 h-6 w-6 animate-spin text-emerald-600" /> : (
        <div className="mt-4 space-y-2" data-testid="free-access-list">
          {active.length === 0 && <p className="text-sm text-muted-foreground" data-testid="free-access-empty">No free access given yet.</p>}
          {active.map((g) => (
            <div key={g.id} data-testid={`grant-${g.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card p-3 text-sm">
              <div className="min-w-0">
                <div className="font-bold"><span className="font-mono text-xs">{g.email}</span>{g.user?.name ? <span className="ml-2 font-normal text-muted-foreground">· {g.user.name}</span> : null}</div>
                <div className="text-xs text-muted-foreground">{LABELS[g.duration] || g.duration}{g.days ? ` · until ${fmt(g.expires_at)}` : ""}{g.note ? ` · ${g.note}` : ""} · given by {g.granted_by} on {fmt(g.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span data-testid={`grant-status-${g.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS[g.status]}`}>{g.status === "waiting" ? "waiting for signup" : g.status}</span>
                <button onClick={() => revoke(g)} disabled={!!busy} data-testid={`grant-revoke-${g.id}`} className="rounded-full border border-border px-3 py-1.5 text-xs font-bold text-muted-foreground hover:text-destructive disabled:opacity-60">{busy === g.id ? "…" : "Remove"}</button>
              </div>
            </div>
          ))}
          {revoked.length > 0 && (
            <details className="text-xs text-muted-foreground"><summary className="cursor-pointer font-semibold">Removed ({revoked.length})</summary>
              <div className="mt-1 space-y-1">{revoked.map((g) => <div key={g.id} className="flex justify-between rounded-xl bg-secondary px-3 py-1.5"><span className="font-mono">{g.email}</span><span>removed {fmt(g.revoked_at)}</span></div>)}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
